/**
 * What Channel A can be asked to transcribe.
 *
 * ### The list is bounded by the font, not by the API
 *
 * Chrome's Web Speech API will happily transcribe Japanese, Arabic, Hindi and
 * Mandarin. This list does not offer them, and that is a decision rather than an
 * oversight: the whole instrument is a variable font carrying mouth state on its
 * `wght` and `wdth` axes, and Roboto Flex covers Latin, Cyrillic, Greek and
 * Vietnamese. Ask for a script it does not have and the browser falls back to a
 * static system font — the transcription would appear and every glyph would be
 * the same weight no matter what the mouth did.
 *
 * That is worse than not offering the language. It looks like the vision channel
 * has died, and the thing it is actually saying is that the type has no axes to
 * move. If a script outside this set is ever wanted, the font comes first.
 *
 * ### Tags are BCP-47 and regional on purpose
 *
 * `pt` is not a language as far as a recogniser is concerned. Brazilian and
 * European Portuguese have different vowel inventories and Chrome ships separate
 * models; asking for the wrong one costs real accuracy on exactly the sounds this
 * project is about.
 */

/** @type {{value: string, label: string}[]} */
export const LANGUAGES = [
  { value: 'en-US', label: 'English (US)' },
  { value: 'en-GB', label: 'English (UK)' },
  { value: 'pt-BR', label: 'Português (Brasil)' },
  { value: 'pt-PT', label: 'Português (Portugal)' },
  { value: 'es-ES', label: 'Español (España)' },
  { value: 'es-MX', label: 'Español (México)' },
  { value: 'fr-FR', label: 'Français' },
  { value: 'it-IT', label: 'Italiano' },
  { value: 'de-DE', label: 'Deutsch' },
  { value: 'nl-NL', label: 'Nederlands' },
  { value: 'sv-SE', label: 'Svenska' },
  { value: 'da-DK', label: 'Dansk' },
  { value: 'nb-NO', label: 'Norsk bokmål' },
  { value: 'fi-FI', label: 'Suomi' },
  { value: 'pl-PL', label: 'Polski' },
  { value: 'cs-CZ', label: 'Čeština' },
  { value: 'ro-RO', label: 'Română' },
  { value: 'tr-TR', label: 'Türkçe' },
  { value: 'ru-RU', label: 'Русский' },
  { value: 'uk-UA', label: 'Українська' },
  { value: 'el-GR', label: 'Ελληνικά' },
];

export const LANGUAGE_TAGS = LANGUAGES.map((l) => l.value);

/**
 * The closest listed tag to what a browser reports, or null.
 *
 * Three passes, because `navigator.language` is not required to be anything in
 * particular: an exact tag, then the primary subtag against a whole tag
 * (`pt` -> `pt-BR`, the first listed variant), then nothing. Null rather than a
 * fallback so the caller decides — "no opinion" and "English" are different
 * answers and only the caller knows which it wants.
 *
 * @param {?string} tag e.g. navigator.language
 * @returns {?string}
 */
export function matchLanguage(tag) {
  const want = String(tag ?? '')
    .trim()
    .toLowerCase();
  if (!want) return null;

  const exact = LANGUAGE_TAGS.find((t) => t.toLowerCase() === want);
  if (exact) return exact;

  const primary = want.split('-')[0];
  return LANGUAGE_TAGS.find((t) => t.toLowerCase().split('-')[0] === primary) ?? null;
}
